import { Stack } from '@fluentui/react';
import React, { useCallback } from 'react';
import { useStyles } from './index.styles';
import {
  MessagesListsProps,
  MessagesSections,
  PivotKeys,
} from 'common/components/MessagesSections';
import { NetworkStatus, useQuery } from '@apollo/client';
import {
  PurchaseOrderMessage,
  PurchaseOrderMessageVariables,
} from './__generated__/PurchaseOrderMessage';
import { NewMessage } from './NewMessage';

import PURCHASE_ORDER_MESSAGE from './PurchaseOrderMessage.graphql';
interface PurchaseOrderMessageProps
  extends Pick<MessagesListsProps, 'userMessageCounts'> {
  purchaseOrderId: string;
  onCreateMessage: () => void;
  dirty?: boolean;
}

export const Messages: React.FC<PurchaseOrderMessageProps> = ({
  purchaseOrderId,
  userMessageCounts,
  onCreateMessage,
  dirty,
}) => {
  const styles = useStyles();
  const [selectedKey, setSelectedKey] = React.useState<string>(
    PivotKeys.received
  );

  const {
    loading: loadingMessages,
    data: messagesData,
    variables: messageListVariables,
    fetchMore,
    refetch,
    networkStatus,
  } = useQuery<PurchaseOrderMessage, PurchaseOrderMessageVariables>(
    PURCHASE_ORDER_MESSAGE,
    {
      variables: {
        id: purchaseOrderId,
        first: 10,
        filter: { _isIncomingMessage: { equalTo: true } },
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-only',
    }
  );

  const onListChange = useCallback(
    async (listName?: string) => {
      if (listName) setSelectedKey(listName);
      const _isOutgoingMessage = { equalTo: listName === PivotKeys.sent };
      await refetch({
        ...messageListVariables,
        filter: {
          _isOutgoingMessage,
        },
      });
    },
    [refetch, messageListVariables]
  );

  const loadMore = useCallback(async () => {
    await fetchMore({
      variables: {
        ...messageListVariables,
        after:
          messagesData?.purchaseOrder?.userMessagesByEntityId.pageInfo
            .endCursor,
      },
    });
  }, [fetchMore, messageListVariables, messagesData]);

  const data =
    networkStatus === NetworkStatus.setVariables
      ? undefined
      : messagesData?.purchaseOrder?.userMessagesByEntityId.nodes;

  return (
    <Stack className={styles.container}>
      <MessagesSections
        userMessageCounts={userMessageCounts}
        entityId={purchaseOrderId}
        data={data || []}
        loadingMessages={loadingMessages}
        onListChange={onListChange}
        onLoadMore={loadMore}
        hasNextPage={
          messagesData?.purchaseOrder?.userMessagesByEntityId?.pageInfo
            ?.hasNextPage
        }
      >
        <NewMessage
          onCreateMessage={onCreateMessage}
          selectedKey={selectedKey}
          purchaseOrderId={purchaseOrderId}
          dirty={dirty}
        />
      </MessagesSections>
    </Stack>
  );
};
